import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { __, _n, sprintf } from '@wordpress/i18n';
import { useQuery } from '@tanstack/react-query';

// store
import { storeSet, useStoreValue } from '../../store';
import { queryKeys } from '../../lib/query-client';

// service
import UsersService from '../../service/users-service';
import ProjectService from '../../service/project-service';

// helpers
import useDebounce from '../../hooks/useDebounce';
import { PROJECT_ROLES, ROLE_LABELS } from '../../helpers/projectRoles';
import { REC_INSTRUMENTS, getInstrumentLabel, getUserFullName } from '../../helpers/recSession';

// components
import ModalDialog from '../ModalDialog';
import UserAvatar from '../UserAvatar';
import CaretIcon from '../../icons/CaretIcon';
import SearchIcon from '../../icons/SearchIcon';
import SendIcon from '../../icons/SendIcon';
import RollingArrowsIcon from '../../icons/RollingArrowsIcon';

import '../../assets/components/ModalInviteRecSessionCollaborators.css';

const MIN_SEARCH_LENGTH = 2;
const NOTE_MAX_LENGTH = 1024;

// ButtonFlat renders a bare <button>, which submits the form it sits in.
const FlatButton = ({ children, ...props }) => (
    <button type="button" className="appButton__flat md" {...props}>{children}</button>
);

/** Team rows and /users/list results, in one shape. */
const toContact = (user) => ({
    userId: user.userId ?? user.id,
    name: user.name,
    surname: user.surname,
    fullName: getUserFullName(user) || user.email || user.invitedEmail || '',
    profession: user.profession ?? null,
    profilePictureUrl: user.profile_picture_url ?? user.profilePictureUrl ?? null,
});

/**
 * The instrument a contact is invited for. A popover with the instrument
 * grid; the choice is only applied on "Ok".
 */
const InstrumentPicker = ({ value, onChange }) => {
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState(value);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;

        const close = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        window.addEventListener('mousedown', close);
        return () => window.removeEventListener('mousedown', close);
    }, [open]);

    const toggle = () => {
        setDraft(value);
        setOpen(prev => !prev);
    };

    return (
        <div className="inviteRecCollab__instrument" ref={ref}>
            <button
                type="button"
                className="inviteRecCollab__instrumentBtn"
                onClick={toggle}
                aria-expanded={open}
            >
                <span>{value ? getInstrumentLabel(value) : __('Seleziona lo strumento', 'soundbetween')}</span>
                <CaretIcon/>
            </button>

            {open && (
                <div className="inviteRecCollab__instrumentPopover" role="dialog">
                    <div className="inviteRecCollab__instrumentGrid">
                        {REC_INSTRUMENTS.map(({ value: type, label, Icon }) => (
                            <button
                                key={type}
                                type="button"
                                className={`appButton__3d inviteRecCollab__instrumentOption${draft === type ? ' inviteRecCollab__instrumentOption--selected' : ''}`}
                                aria-pressed={draft === type}
                                onClick={() => setDraft(type)}
                            >
                                <span><Icon/>{label}</span>
                            </button>
                        ))}
                    </div>
                    <div className="inviteRecCollab__instrumentActions">
                        <FlatButton
                            disabled={!draft}
                            onClick={() => {
                                onChange(draft);
                                setOpen(false);
                            }}
                        >
                            {__('Ok', 'soundbetween')}
                        </FlatButton>
                        <FlatButton onClick={() => setOpen(false)}>
                            {__('Annulla', 'soundbetween')}
                        </FlatButton>
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * "Invita Collaboratori" for one rec session: pick contacts, say which
 * instrument each one plays, and they get a layer of their own in the session.
 */
const ModalInviteRecSessionCollaborators = ({ projectId, recSession, onClose }) => {
    const currentUserId = useStoreValue('userData')?.id || null;
    const [networkOpen, setNetworkOpen] = useState(true);
    const [keyword, setKeyword] = useState('');
    const debouncedKeyword = useDebounce(keyword.trim(), 300);
    // userId → { contact, instrument }
    const [picked, setPicked] = useState({});
    const [role, setRole] = useState(PROJECT_ROLES.MUSICIAN);
    const [note, setNote] = useState('');
    const [sending, setSending] = useState(false);

    const { data: team = [] } = useQuery({
        queryKey: queryKeys.projectTeam(projectId),
        queryFn: () => ProjectService.getProjectTeamAsync(projectId),
        enabled: Boolean(projectId),
    });

    const searchEnabled = debouncedKeyword.length >= MIN_SEARCH_LENGTH;
    const { data: searchResults = [], isFetching: searching } = useQuery({
        queryKey: ['users', 'search', debouncedKeyword],
        queryFn: () => UsersService.getUsersAsync([['name', debouncedKeyword]]),
        enabled: searchEnabled,
    });

    // Already in this session: inviting them again would add a second layer.
    const inSession = useMemo(() => new Set(
        (recSession?.auxLayers || []).map(l => l.assignedUser?.userId).filter(Boolean)
    ), [recSession]);

    // With no search the project team is the network at hand; a search reaches
    // every user. Picked contacts stay listed so they can still be changed.
    const contacts = useMemo(() => {
        const source = searchEnabled ? (searchResults || []) : (team || []).filter(row => row.userId);
        const list = source
            .map(toContact)
            .filter(c => c.userId && c.userId !== currentUserId && !inSession.has(c.userId));

        const listed = new Set(list.map(c => c.userId));
        const pickedOnly = Object.values(picked)
            .map(p => p.contact)
            .filter(c => !listed.has(c.userId));

        return [...pickedOnly, ...list];
    }, [searchEnabled, searchResults, team, currentUserId, inSession, picked]);

    const pickedList = Object.values(picked);
    const missingInstrument = pickedList.some(p => !p.instrument);
    const canSend = pickedList.length > 0 && !missingInstrument && !sending;

    const togglePick = (contact) => {
        setPicked(prev => {
            const next = { ...prev };
            if (next[contact.userId]) {
                delete next[contact.userId];
            } else {
                next[contact.userId] = { contact, instrument: null };
            }
            return next;
        });
    };

    const setInstrument = (contact, instrument) => {
        setPicked(prev => ({
            ...prev,
            [contact.userId]: { contact, instrument },
        }));
    };

    const handleSend = useCallback(async (e) => {
        e?.preventDefault?.();
        if (!canSend) return;

        setSending(true);
        try {
            await storeSet('inviteRecSessionCollaborators', projectId, recSession.id, {
                collaborators: pickedList.map(p => ({ user: p.contact, instrument: p.instrument })),
                role,
                note: note.trim(),
            });
            onClose();
        } finally {
            setSending(false);
        }
    }, [canSend, projectId, recSession, pickedList, role, note, onClose]);

    const networkSummary = pickedList.length
        ? sprintf(_n('%d contatto selezionato', '%d contatti selezionati', pickedList.length, 'soundbetween'), pickedList.length)
        : __('Scegli contatti dal tuo Network', 'soundbetween');

    return (
        <ModalDialog
            additionalClasses={['modalInviteRecCollab']}
            header={__('Invita Collaboratori', 'soundbetween')}
            visible={true}
            width={774}
            onHide={onClose}
        >
            <form className="inviteRecCollab" onSubmit={handleSend}>
                <p className="inviteRecCollab__intro">
                    {__('Invita musicisti, mix engineer o sound designer a collaborare a questo progetto.', 'soundbetween')}
                </p>

                <div className={`inviteRecCollab__network${networkOpen ? ' inviteRecCollab__network--open' : ''}`}>
                    <button
                        type="button"
                        className="inviteRecCollab__networkToggle"
                        onClick={() => setNetworkOpen(prev => !prev)}
                        aria-expanded={networkOpen}
                    >
                        <span>{networkOpen ? __('Scegli contatti dal tuo Network', 'soundbetween') : networkSummary}</span>
                        <CaretIcon/>
                    </button>

                    {networkOpen && (
                        <>
                            <label className="inviteRecCollab__search">
                                <input
                                    type="search"
                                    value={keyword}
                                    placeholder={__('Cerca', 'soundbetween')}
                                    aria-label={__('Cerca contatti', 'soundbetween')}
                                    onChange={e => setKeyword(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
                                />
                                <span className="inviteRecCollab__searchIcon"><SearchIcon/></span>
                            </label>

                            <ul className="inviteRecCollab__contacts">
                                {contacts.length === 0 && (
                                    <li className="inviteRecCollab__empty">
                                        {searching
                                            ? __('Ricerca in corso…', 'soundbetween')
                                            : searchEnabled
                                                ? __('Nessun risultato trovato', 'soundbetween')
                                                : __('Nessun contatto nel team: cerca un utente per nome.', 'soundbetween')}
                                    </li>
                                )}
                                {contacts.map(contact => {
                                    const entry = picked[contact.userId];
                                    const checked = Boolean(entry);
                                    return (
                                        <li
                                            key={contact.userId}
                                            className={`inviteRecCollab__contact${checked ? ' inviteRecCollab__contact--checked' : ''}`}
                                        >
                                            <label className="inviteRecCollab__contactCheck">
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => togglePick(contact)}
                                                />
                                                <UserAvatar url={contact.profilePictureUrl} name={contact.fullName}/>
                                                <span className="inviteRecCollab__contactText">
                                                    <span className="inviteRecCollab__contactName">{contact.fullName}</span>
                                                    {contact.profession && (
                                                        <span className="inviteRecCollab__contactMeta">{contact.profession}</span>
                                                    )}
                                                </span>
                                            </label>
                                            <InstrumentPicker
                                                value={entry?.instrument ?? null}
                                                onChange={instrument => setInstrument(contact, instrument)}
                                            />
                                        </li>
                                    );
                                })}
                            </ul>

                            <div className="inviteRecCollab__networkActions">
                                <FlatButton
                                    disabled={pickedList.length === 0}
                                    onClick={() => setNetworkOpen(false)}
                                >
                                    {__('Conferma', 'soundbetween')}
                                </FlatButton>
                                <button
                                    type="button"
                                    className="inviteRecCollab__reset"
                                    onClick={() => setPicked({})}
                                    disabled={pickedList.length === 0}
                                    title={__('Azzera la selezione', 'soundbetween')}
                                    aria-label={__('Azzera la selezione', 'soundbetween')}
                                >
                                    <RollingArrowsIcon/>
                                </button>
                            </div>
                        </>
                    )}
                </div>

                {missingInstrument && (
                    <p className="inviteRecCollab__hint" role="status">
                        {__('Scegli lo strumento per ogni contatto selezionato.', 'soundbetween')}
                    </p>
                )}

                <label className="inviteRecCollab__field">
                    <span className="inviteRecCollab__label">{__('Ruolo', 'soundbetween')}</span>
                    <select value={role} onChange={e => setRole(e.target.value)}>
                        {Object.values(PROJECT_ROLES).map(value => (
                            <option key={value} value={value}>{ROLE_LABELS[value]}</option>
                        ))}
                    </select>
                    <span className="inviteRecCollab__fieldHelp">
                        {__('Vale per chi non fa ancora parte del team del progetto.', 'soundbetween')}
                    </span>
                </label>

                <label className="inviteRecCollab__field">
                    <span className="inviteRecCollab__label">{__('Messaggio', 'soundbetween')}</span>
                    <textarea
                        rows={5}
                        value={note}
                        maxLength={NOTE_MAX_LENGTH}
                        onChange={e => setNote(e.target.value)}
                    />
                </label>

                <div className="inviteRecCollab__actions">
                    <button type="submit" className="inviteRecCollab__send" disabled={!canSend}>
                        <SendIcon/>
                        <span>{sending ? __('Invio…', 'soundbetween') : __('Manda invito', 'soundbetween')}</span>
                    </button>
                    <FlatButton onClick={onClose}>
                        {__('Annulla', 'soundbetween')}
                    </FlatButton>
                </div>
            </form>
        </ModalDialog>
    );
};

export default ModalInviteRecSessionCollaborators;
